import { gql } from '@apollo/client';

import { CART_FRAGMENT, PRODUCT_FRAGMENT } from 'src/fragments/fragments';

/**
 * Shared so a caller can add to the basket without importing a whole page component.
 *
 * It used to live in ProductLayout, which pulls in ProductCard, StepsUpsell and the
 * rest of the product page; importing the mutation from there dragged all of that into
 * any bundle that needed it — including the LED landing page, where payload size is
 * paid for directly in ad spend.
 */
export const ADD_TO_CART = gql`
  ${CART_FRAGMENT}
  ${PRODUCT_FRAGMENT}
  mutation ATC($input: AddToCartInput!) {
    addToCart(input: $input) {
      cart {
        ...CartFragment
      }
    }
  }
`;

export const CART = gql`
  ${CART_FRAGMENT}
  ${PRODUCT_FRAGMENT}
  query Cart {
    cart {
      ...CartFragment
    }
  }
`;

/**
 * One request for a whole batch of quantity changes, including removals.
 *
 * WooCommerce's `WC_Cart::set_quantity` deletes the line when the quantity is 0, so a
 * removal is just a quantity change and does not need `removeItemsFromCart` — which is
 * what lets the cart drawer flush every pending edit in a single round-trip.
 */
export const UPDATE_ITEM_QUANTITIES = gql`
  ${CART_FRAGMENT}
  ${PRODUCT_FRAGMENT}
  mutation UpdateItemQuantities($input: UpdateItemQuantitiesInput!) {
    updateItemQuantities(input: $input) {
      cart {
        ...CartFragment
      }
    }
  }
`;

/**
 * Cheapest in-stock products within [minPrice, maxPrice], driven by the cart drawer's
 * current gap to the next free-shipping/free-gift threshold. Cart mounts on every page
 * (just slid off-screen when closed), so this resolves well before the drawer opens.
 */
export const GAP_CLOSER_CANDIDATES = gql`
  query GapCloserCandidates(
    $minPrice: Float
    $maxPrice: Float
    $exclude: [Int]
  ) {
    products(
      first: 4
      where: {
        stockStatus: [IN_STOCK]
        orderby: [{ field: PRICE, order: ASC }]
        minPrice: $minPrice
        maxPrice: $maxPrice
        exclude: $exclude
      }
    ) {
      nodes {
        ... on SimpleProduct {
          databaseId
          name
          uri
          price(format: RAW)
          image {
            sourceUrl(size: WOOCOMMERCE_THUMBNAIL)
          }
        }
      }
    }
  }
`;
